import React from "react";
import PropTypes from "prop-types";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment-timezone";
import LessonWrapper from "./LessonWrapper";

import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/sass/styles.scss";

const localizer = momentLocalizer(moment.tz.setDefault("Europe/Warsaw"));
const ATTENDED_EVENT_BACKGROUND = "#0c4946";
const DO_NOT_CHARGE_EVENT_BACKGROUND = "#6c757d";

function DashboardCalendarEvent({ event }) {
  const { calendarContext } = event;

  return (
    <LessonWrapper
      event={event}
      display="calendar-event"
      students={calendarContext.students}
      teachers={calendarContext.teachers}
      onSubmit={calendarContext.onSubmit}
      deleteLessonRegister={calendarContext.deleteLessonRegister}
    />
  );
}

DashboardCalendarEvent.propTypes = {
  event: PropTypes.shape({
    scheduledLesson: PropTypes.object.isRequired,
    calendarContext: PropTypes.shape({
      students: PropTypes.array.isRequired,
      teachers: PropTypes.array.isRequired,
      onSubmit: PropTypes.func.isRequired,
      deleteLessonRegister: PropTypes.func.isRequired,
    }).isRequired,
  }).isRequired,
};

function DashboardCalendar({
  scheduledClasses,
  students,
  teachers,
  onSubmit,
  deleteLessonRegister,
  onVisibleDateChange,
}) {
  const events = scheduledClasses.flatMap(scheduledClass => (
    scheduledClass.scheduledLessons.map((scheduledLesson) => {
      const duration = parseInt(scheduledLesson.defaultLessonDetails.classTypeId.slice(1), 10);
      const start = moment(
        scheduledLesson.lessonRegister
          ? `${scheduledLesson.lessonRegister.lessonDetails.date} ${scheduledLesson.lessonRegister.lessonDetails.startTime}`
          : `${scheduledLesson.defaultLessonDetails.date} ${scheduledLesson.defaultLessonDetails.startTime}`,
        "YYYY-MM-DD HH:mm",
      ).toDate();
      const end = moment(start).add(duration, "minutes").toDate();

      return {
        id: scheduledLesson.id,
        title: `${scheduledLesson.defaultLessonDetails.classTypeId}`,
        start,
        end,
        scheduledLesson,
        calendarContext: {
          students,
          teachers,
          onSubmit,
          deleteLessonRegister,
        },
      };
    })
  ));

  const handleResize = () => {
    if (window.innerWidth < 768) {
      return "day";
    }

    return "week";
  };

  const currentRoundedTime = () => {
    const currentTime = new Date();
    const minutes = currentTime.getMinutes();
    const roundedMinutes = Math.round(minutes / 30) * 30;
    currentTime.setMinutes(roundedMinutes);
    return currentTime;
  };

  const dayStart = moment().hours(8).minutes(0).toDate();
  const dayEnd = moment().hours(20).minutes(0).toDate();
  const eventPropGetter = ({ scheduledLesson }) => {
    const doNotCharge = scheduledLesson.lessonRegister &&
      scheduledLesson.lessonRegister.lessonDetails &&
      scheduledLesson.lessonRegister.lessonDetails.doNotCharge;

    if (doNotCharge) {
      return {
        className: "dashboard-do-not-charge-event",
        style: { backgroundColor: DO_NOT_CHARGE_EVENT_BACKGROUND },
      };
    }

    return scheduledLesson.lessonRegister
      ? {
        className: "dashboard-attended-event",
        style: { backgroundColor: ATTENDED_EVENT_BACKGROUND },
      }
      : {};
  };

  return (
    <div className="my-calendar">
      <Calendar
        localizer={localizer}
        events={events}
        views={["month", "week", "day"]}
        onNavigate={onVisibleDateChange}
        startAccessor="start"
        endAccessor="end"
        style={{ height: 750 }}
        min={dayStart}
        max={dayEnd}
        step={20}
        timeslots={3}
        defaultView={handleResize()}
        scrollToTime={currentRoundedTime()}
        eventPropGetter={eventPropGetter}
        components={{
          event: DashboardCalendarEvent,
        }}
      />
    </div>
  );
}

DashboardCalendar.propTypes = {
  scheduledClasses: PropTypes.array.isRequired,
  students: PropTypes.array.isRequired,
  teachers: PropTypes.array.isRequired,
  onSubmit: PropTypes.func.isRequired,
  deleteLessonRegister: PropTypes.func.isRequired,
  onVisibleDateChange: PropTypes.func,
};

DashboardCalendar.defaultProps = {
  onVisibleDateChange: () => {},
};

export default DashboardCalendar;
